import { useMutation } from '@tanstack/react-query'
import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

/** Exactly the body `Api::RegistrationsController#create` permits. */
export interface SignupInput {
  invitation_code: string
  account_name: string
  owner_first_name: string
  owner_last_name: string
  email: string
  phone: string
  address: string
  language: 'th' | 'en'
  // EP-160 (section 2.7, AC-10): the applicant ticked the terms box. The
  // server refuses anything but true.
  terms_accepted: boolean
  // EP-160 T160-201w (S9, D4): a 30-day `setup` grant for the FlowRent team,
  // created with the account. Ticked by default on the form.
  support_onboarding_grant: boolean
}

export interface SignupResponse {
  data: {
    account: { id: number; name: string; slug: string }
    email: string
    verification_sent: boolean
  }
}

/** Tenant error envelope: `{ error: { code, message, details? } }`. */
export interface SignupErrorBody {
  error?: {
    code?: string
    message?: string
    details?: Record<string, string[]>
  }
}

/**
 * EP-144 T144-004: public sign-up.
 *
 * Bare axios, not the authenticated `api` client: the applicant has no token
 * and no account yet, so the interceptor's refresh/redirect machinery has
 * nothing to work with. Same shape as `usePasswordReset`, the other
 * pre-authentication hook.
 */
export function useSignup() {
  return useMutation<SignupResponse, unknown, SignupInput>({
    mutationFn: async (input: SignupInput) => {
      const { data } = await axios.post<SignupResponse>(`${API_BASE_URL}/api/registrations`, {
        registration: input,
      })
      return data
    },
  })
}
